/**
 * Theme front-end bootstrap.
 *
 * Kept intentionally minimal while the redesign is in progress.
 *
 * @package LecardoVein
 */

import { initAccessibilityControls } from './components/accessibility-controls';
import { initHeaderMobileMenu } from './components/header-mobile-menu';
import { initHeaderSticky } from './components/header-sticky';
import { initSpecialistFinder } from './components/specialist-finder';
import { initReviewModal } from './components/review-modal';
import { initModalTriggers } from './components/modal';
import { initAppointmentModal } from './components/appointment-modal';
import { initSpecialistFinderModal } from './components/specialist-finder-modal';
import { initSiteModalCf7 } from './components/site-modal-cf7';
import { initSiteModalSelects } from './components/site-modal-selects';
import { initContactsMap } from './components/contacts-map';
import { initDoctors } from './components/doctors';
import { initFaqAccordion } from './components/faq-accordion';
import { initContactCallbackForm } from './components/contact-callback-form';
import {
	initDirectionServices,
	initDirectionServiceGalleries,
} from './components/direction-services';
import { initServiceDoctors } from './components/service-doctors';
import { initHeroDeferredVideo } from './components/hero-video';

document.addEventListener('DOMContentLoaded', () => {
	initHeroDeferredVideo();
});

window.addEventListener('load', () => {
	if (window.Input) {
		new window.Input().listenner(
			Array.from(
				document.querySelectorAll('input:not([name="appointment-date"])')
			)
		);
	}

	initArrowUp();
	initHeaderSubmenus();
	initHeaderMobileMenu();
	initHeaderSticky();
	initHeaderSearch();
	initAccessibilityControls();
	initSpecialistFinder();
	initReviewModal();
	initModalTriggers();
	initSiteModalSelects();
	initSiteModalCf7();
	initAppointmentModal();
	initSpecialistFinderModal();
	initContactsMap();
	initDoctors();
	initFaqAccordion();
	initContactCallbackForm();
	initDirectionServices();
	initDirectionServiceGalleries();
	initServiceDoctors();
});

['wpcf7mailsent', 'wpcf7invalid', 'wpcf7spam', 'wpcf7mailfailed'].forEach(
	(event_name) => {
		document.addEventListener(event_name, () => {
			initContactCallbackForm();
		});
	}
);

/**
 * Toggle desktop header submenus by click and keyboard.
 *
 * Hover and focus visibility is handled in CSS; this class preserves an open
 * submenu after clicking and closes it on an outside click or Escape.
 *
 * @return {void}
 */
function initHeaderSubmenus() {
	const menu = document.querySelector('.header__menu');

	if (!menu) {
		return;
	}

	const items = Array.from(menu.querySelectorAll(':scope > .menu-item-has-children'));

	const closeItem = (item) => {
		item.classList.remove('is-open');
		item.querySelector(':scope > a')?.setAttribute('aria-expanded', 'false');
	};

	const closeAll = (except = null) => {
		items.forEach((item) => {
			if (item !== except) {
				closeItem(item);
			}
		});
	};

	items.forEach((item) => {
		const link = item.querySelector(':scope > a');
		const submenu = item.querySelector(':scope > .sub-menu');

		if (!link || !submenu) {
			return;
		}

		link.setAttribute('aria-haspopup', 'true');
		link.setAttribute('aria-expanded', 'false');

		link.addEventListener('click', (event) => {
			event.preventDefault();

			const shouldOpen = !item.classList.contains('is-open');
			closeAll(item);
			item.classList.toggle('is-open', shouldOpen);
			link.setAttribute('aria-expanded', String(shouldOpen));
		});
	});

	document.addEventListener('click', (event) => {
		if (!menu.contains(event.target)) {
			closeAll();
		}
	});

	menu.addEventListener('keydown', (event) => {
		if ('Escape' !== event.key) {
			return;
		}

		const openItem = menu.querySelector(':scope > .menu-item-has-children.is-open');

		if (openItem) {
			const link = openItem.querySelector(':scope > a');
			closeItem(openItem);
			link?.focus();
		}
	});
}

/**
 * Toggle the desktop header search form without changing header layout.
 *
 * @return {void}
 */
function initHeaderSearch() {
	const searches = document.querySelectorAll('[data-header-search]');

	searches.forEach((search) => {
		const toggle = search.querySelector('[data-header-search-toggle]');
		const form = search.querySelector('.search-form');
		const input = form?.querySelector('input[type="search"]');

		if (!toggle || !form || !input) {
			return;
		}

		const setOpen = (isOpen, returnFocus = false) => {
			search.classList.toggle('is-open', isOpen);
			toggle.setAttribute('aria-expanded', String(isOpen));

			if (isOpen) {
				window.requestAnimationFrame(() => input.focus());
			} else if (returnFocus) {
				toggle.focus();
			}
		};

		toggle.addEventListener('click', () => {
			setOpen(!search.classList.contains('is-open'));
		});

		document.addEventListener('click', (event) => {
			if (!search.contains(event.target)) {
				setOpen(false);
			}
		});

		search.addEventListener('keydown', (event) => {
			if ('Escape' === event.key) {
				setOpen(false, true);
			}
		});
	});
}

/**
 * Scroll-to-top fixed button.
 *
 * @return {void}
 */
function initArrowUp() {
	const arrowUp = document.querySelector('.arrow-up');

	if (!arrowUp) {
		return;
	}

	const updateVisibility = () => {
		arrowUp.style.display = window.scrollY < window.innerHeight ? 'none' : '';
	};

	arrowUp.addEventListener('click', () => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	});

	updateVisibility();
	window.addEventListener('scroll', updateVisibility, { passive: true });
}
